import { formatRelativeTime } from '../../utils/format';

export const UNAVAILABLE = 'Not available';
export const PREVIEW_UNAVAILABLE =
  'Document preview is not available from the current Custodian API.';

export function custodianPackageDetailPath(packageId) {
  return `/custodian/assets/${encodeURIComponent(packageId)}`;
}

export function displayValue(value) {
  if (value === undefined || value === null || value === '') {
    return UNAVAILABLE;
  }
  return String(value);
}

export function packageAuthority(detail) {
  return detail?.authority_status || detail?.authority_state || null;
}

export function packageCustody(detail) {
  return detail?.custody_status || detail?.custody_state || null;
}

export function packageAdmission(detail, admissionStatus) {
  return (
    detail?.admission_state ||
    admissionStatus?.vault_assets?.[0]?.admission_state ||
    null
  );
}

export function canRecordAuthorityDecision(detail) {
  if (!detail) return false;
  return packageAuthority(detail) === 'PENDING';
}

export function canAdmitPackage(detail) {
  if (!detail) return false;
  const authority = packageAuthority(detail);
  const custody = packageCustody(detail);
  const admission = detail.admission_state;
  return (
    authority === 'APPROVED' &&
    custody !== 'ACCEPTED' &&
    admission !== 'AUTHORITATIVE'
  );
}

export function isPackageAdmitted(detail, admissionStatus) {
  if (!detail && !admissionStatus) return false;
  const admission = packageAdmission(detail, admissionStatus);
  const custody = packageCustody(detail) || admissionStatus?.custody_status;
  return admission === 'AUTHORITATIVE' || custody === 'ACCEPTED';
}

export function evidenceReadinessLabel(detail) {
  if (!detail) return UNAVAILABLE;
  if (detail.evidence_bundle_id) return 'AVAILABLE';
  if (detail.evidence_total > 0) {
    return `${detail.evidence_satisfied ?? 0} / ${detail.evidence_total}`;
  }
  if (detail.evidence_satisfied > 0) {
    return `${detail.evidence_satisfied} / ${detail.evidence_total ?? detail.evidence_satisfied}`;
  }
  return UNAVAILABLE;
}

export function evidenceBundleLabel(detail) {
  if (detail?.evidence_bundle_id) return 'AVAILABLE';
  return UNAVAILABLE;
}

export function evidenceCreationStatus(detail) {
  if (detail?.evidence_bundle_id) return 'Evidence Bundle created';
  return 'Not created — admission pending';
}

export function providerEvidenceCheckCount(detail) {
  const total = Number(detail?.evidence_total);
  const satisfied = Number(detail?.evidence_satisfied);
  if (!Number.isFinite(total) || total < 0) {
    return { satisfied: 0, total: 0 };
  }
  return {
    satisfied: Number.isFinite(satisfied) && satisfied > 0 ? satisfied : 0,
    total,
  };
}

export function fileIntegrityStatus(file) {
  if (!file) return UNAVAILABLE;
  if (file.upload_status === 'COMPLETE' && file.content_sha256) {
    return 'COMPLETE / verified';
  }
  return displayValue(file.upload_status);
}

/**
 * Checklist rows the Custodian package API can actually support.
 * Does not invent named provider-evidence checks that are not returned.
 */
export function custodianEvidenceChecklistItems(detail, admissionStatus) {
  const files = Array.isArray(detail?.files) ? detail.files : [];
  const items = files.map((file, index) => ({
    id: file.id || `file-${index}`,
    label: file.file_name || 'Source file',
    done: file.upload_status === 'COMPLETE' && Boolean(file.content_sha256),
  }));
  items.push({
    id: 'hash-integrity',
    label: 'Hash integrity',
    done: files.some((file) => Boolean(file.content_sha256)),
  });
  const bundleId =
    detail?.evidence_bundle_id ||
    admissionStatus?.vault_assets?.[0]?.evidence_bundle_id;
  items.push({
    id: 'evidence-bundle',
    label: 'Evidence bundle',
    done: Boolean(bundleId),
  });
  return items;
}

/**
 * Visual workflow for live Custodian review. Completion uses only returned fields.
 */
export function custodianWorkflowSteps(detail, admissionStatus) {
  const recommendation = detail?.recommendation || detail?.provider_decision;
  const routed = detail?.task_status === 'ROUTED';
  const authority = packageAuthority(detail);
  const custody = packageCustody(detail) || admissionStatus?.custody_status;
  const admission = packageAdmission(detail, admissionStatus);
  const rejected = authority === 'REJECTED';
  const approved = authority === 'APPROVED';
  const authoritative = admission === 'AUTHORITATIVE';
  const accepted = custody === 'ACCEPTED';
  const pastProvider =
    recommendation === 'CANDIDATE' || routed || approved || rejected || authoritative;
  const pastRoute = routed || approved || rejected || authoritative || accepted;
  const routedAt = formatRelativeTime(detail?.routed_at);

  return [
    {
      id: 'candidate',
      label: 'Provider CANDIDATE',
      shortLabel: 'CANDIDATE',
      complete: Boolean(pastProvider),
      detail: pastProvider ? recommendation || 'CANDIDATE' : undefined,
    },
    {
      id: 'routed',
      label: 'Routed to Custodian',
      shortLabel: 'ROUTED',
      complete: Boolean(pastRoute),
      detail: pastRoute ? (routedAt === '—' ? 'Routed' : routedAt) : undefined,
    },
    {
      id: 'authority',
      label: rejected
        ? 'Authority REJECTED'
        : approved
          ? 'Authority APPROVED'
          : 'Authority PENDING',
      shortLabel: rejected ? 'REJECTED' : approved ? 'APPROVED' : 'PENDING',
      complete: approved || rejected,
      detail: rejected ? 'Rejected' : approved ? 'Approved' : undefined,
    },
    {
      id: 'admit',
      label: 'Technical admission',
      shortLabel: 'Admit',
      complete: authoritative || accepted,
      detail: authoritative || accepted ? 'Admitted' : undefined,
    },
    {
      id: 'authoritative',
      label: 'Authoritative vault',
      shortLabel: 'Vault',
      complete: Boolean(authoritative),
      detail: authoritative ? 'Authoritative' : undefined,
    },
    {
      id: 'custody',
      label: 'Custody accepted',
      shortLabel: 'Holder',
      complete: Boolean(accepted),
      detail: accepted ? 'Accepted' : undefined,
    },
  ];
}
